/**
 * Pagination Component
 *
 * Accessible pagination for lists and tables.
 * Keyboard navigable with proper ARIA labels.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface PaginationProps extends ComponentPropsWithoutRef<'nav'> {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Number of page buttons to show around current page
   * @default 1
   */
  siblingCount?: number;
  /**
   * Show first/last page buttons
   * @default true
   */
  showFirstLast?: boolean;
  /**
   * Accessible label for the navigation
   * @default "Pagination"
   */
  ariaLabel?: string;
}

export interface PaginationItemProps extends ComponentPropsWithoutRef<'button'> {
  /**
   * Whether this item is the current page
   */
  active?: boolean;
  /**
   * Whether this item is disabled
   */
  disabled?: boolean;
}

/**
 * Generate page numbers to display
 */
function generatePagination(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): (number | 'ellipsis')[] {
  const totalNumbers = siblingCount * 2 + 3; // siblings + current + first + last
  const totalBlocks = totalNumbers + 2; // + 2 ellipsis

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [1, 'ellipsis', ...rightRange];
  }

  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages];
  }

  return [];
}

/**
 * Pagination Item (internal component)
 */
const PaginationItem = forwardRef<HTMLButtonElement, PaginationItemProps>(
  ({ className, active, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      className={cn(
        // BFSG compliant 44x44px touch target
        'inline-flex h-11 min-w-[44px] items-center justify-center rounded-md px-4',
        'text-sm font-medium transition-colors',

        // Focus ring - WCAG 2.1 AA
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900',

        // Active state - WCAG AA contrast
        active && 'bg-primary-600 text-white',
        active && 'hover:bg-primary-700',

        // Inactive state
        !active && 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800',

        // Disabled state
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',

        className
      )}
      {...props}
    />
  )
);
PaginationItem.displayName = 'PaginationItem';

/**
 * Pagination Component
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={3}
 *   totalPages={10}
 *   onPageChange={(page) => console.log('Go to page', page)}
 *   siblingCount={1}
 * />
 * ```
 */
const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      className,
      currentPage,
      totalPages,
      onPageChange,
      siblingCount = 1,
      showFirstLast = true,
      ariaLabel,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const pages = generatePagination(currentPage, totalPages, siblingCount);

    const handlePrevious = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNext = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label={ariaLabel ?? t('a11y.paginationLabel')}
        className={cn('flex items-center justify-center gap-1', className)}
        {...props}
      >
        {/* Previous Button */}
        {showFirstLast && (
          <PaginationItem
            onClick={handlePrevious}
            disabled={currentPage === 1}
            aria-label={t('a11y.prevPage')}
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="sr-only">{t('a11y.previous')}</span>
          </PaginationItem>
        )}

        {/* Page Numbers */}
        {pages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="inline-flex h-11 min-w-[44px] items-center justify-center px-2 text-neutral-400 dark:text-neutral-500"
                aria-hidden="true"
              >
                <MoreHorizontal className="h-5 w-5" />
              </span>
            );
          }

          return (
            <PaginationItem
              key={page}
              onClick={() => onPageChange(page)}
              active={currentPage === page}
              aria-label={t('a11y.goToPage', { page })}
            >
              {page}
            </PaginationItem>
          );
        })}

        {/* Next Button */}
        {showFirstLast && (
          <PaginationItem
            onClick={handleNext}
            disabled={currentPage === totalPages}
            aria-label={t('a11y.nextPage')}
          >
            <ChevronRight className="h-5 w-5" />
            <span className="sr-only">{t('a11y.next')}</span>
          </PaginationItem>
        )}
      </nav>
    );
  }
);
Pagination.displayName = 'Pagination';

export { Pagination, PaginationItem };
